import { useState, useEffect, createContext, useContext } from 'react';

import { availableFonts } from '../components/AddTextSelector';

const CanvasContext = createContext();

export function CanvasProvider({ children }) {
  const [layers, setLayers] = useState([]);

  const createLayer = (type, data) => {
    const baseLayer = {
      id: layers.length + 1,
      type,
      visible: true,
      x: 0,
      y: 0,
      scale: 1,
      rotation: 0,
      mirrored: false,
      zIndex: layers.length + 1,
    };
  
    switch (type) {
      case 'asset':
        return {
          ...baseLayer,
          name: data.name || 'Untitled Asset',
          path: data.path,
          category: data.category || 'uncategorized',
          baseWidth: data.baseWidth || 100,
          baseHeight: data.baseHeight || 100,
          scale: typeof data.scale === 'number' ? data.scale : baseLayer.scale,
        };
      
      case 'text':
        return {
          ...baseLayer,
          text: data.text || '',
          font: data.font || 'Arial',
          color: data.color || '#000000',
          outlineColor: data.outlineColor || null,
          baseWidth: data.baseWidth || 200,
          baseHeight: data.baseHeight || 200,
        };
      
      default:
        return baseLayer;
    }
  };
  
  const addAssetLayer = (asset) => {
    const img = new Image();
    img.onload = () => {
      const aspectRatio = img.naturalWidth / img.naturalHeight;
      const baseWidth = asset.category === 'custom' ? 100 : 200;
      const baseHeight = asset.category === 'custom' ? 100 : 200;
      
      console.log('asset')
      console.log(asset)
      const initialScale = asset.category?.toLowerCase() === 'backgrounds' || asset.category?.toLowerCase() === 'template' ? 1 : 0.25;

      const layer = createLayer('asset', {
        ...asset,
        baseWidth,
        baseHeight,
        scale: initialScale,
      });
      setLayers(prev => [...prev, layer]);
    };
    img.src = asset.category === 'custom' ? asset.path : `./assets/${asset.path}`;
    return null;
  }

  const addTextLayer = (text, font, color, outlineColor) => {
    const layer = createLayer('text', {
      text,
      font: font || 'Arial',
      color: color || '#000000',
      outlineColor: outlineColor || null,
      baseWidth: 200,
      baseHeight: 200,
    });
    setLayers(prev => [...prev, layer]);
    return layer;
  }

  const mirrorLayer = (layerId) => {
    setLayers(prev => prev.map(layer => 
      layer.id === layerId 
        ? { ...layer, mirrored: !layer.mirrored }
        : layer
    ));
  }

  const resizeLayer = (layerId, newScale, newX, newY) => {
    setLayers(prev => prev.map(layer => 
      layer.id === layerId 
        ? { ...layer, scale: newScale, x: newX || layer.x, y: newY || layer.y }
        : layer
    ));
  }

  const removeLayer = (layerId) => {
    setLayers(prev => prev.filter(layer => layer.id !== layerId));
  }

  const clearCanvas = () => {
    setLayers([]);
  }

  const downloadCanvas = async () => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    canvas.width = 800;
    canvas.height = 800;
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Sort layers by zIndex to maintain proper rendering order
    const sortedLayers = [...layers].sort((a, b) => a.zIndex - b.zIndex);

    for (const layer of sortedLayers) {
      if (!layer.visible) {
        continue;
      }

      if (layer.type === 'asset') {
        await new Promise((resolve) => {
          const assetImg = new Image();
          assetImg.onload = () => {
            ctx.save();
            
            // Get actual DOM element positions and sizes
            const canvasContainer = document.querySelector('.canvas-container');
            const layerElement = document.getElementById(`canvas-layer-${layer.id}`);
            const imgElement = layerElement?.querySelector('img');
            
            if (canvasContainer && imgElement) {
              const containerRect = canvasContainer.getBoundingClientRect();
              const imgRect = imgElement.getBoundingClientRect();
              
              // Convert DOM coordinates to canvas coordinates
              const scaleX = 800 / containerRect.width;
              const scaleY = 800 / containerRect.height;
              
              // Calculate object-contain dimensions to maintain aspect ratio
              const imgAspect = assetImg.naturalWidth / assetImg.naturalHeight;
              const domWidth = imgRect.width;
              const domHeight = imgRect.height;
              const domAspect = domWidth / domHeight;
              
              let renderWidth, renderHeight;
              if (imgAspect > domAspect) {
                renderWidth = domWidth;
                renderHeight = domWidth / imgAspect;
              } else {
                renderHeight = domHeight;
                renderWidth = domHeight * imgAspect;
              }
              
              const x = (imgRect.left - containerRect.left + (domWidth - renderWidth) / 2) * scaleX;
              const y = (imgRect.top - containerRect.top + (domHeight - renderHeight) / 2) * scaleY;
              const width = renderWidth * scaleX;
              const height = renderHeight * scaleY;
              
              const centerX = x + width / 2;
              const centerY = y + height / 2;
              
              ctx.translate(centerX, centerY);
              ctx.rotate(layer.rotation * Math.PI / 180);
              if (layer.mirrored) {
                ctx.scale(-1, 1);
              }
              ctx.translate(-centerX, -centerY);
              
              ctx.drawImage(assetImg, x, y, width, height);
              
              // Add character shadow if it's a character
              if (layer.category === 'characters') {
                ctx.globalAlpha = 0.4;
                ctx.fillStyle = 'black';
                ctx.filter = 'blur(20px)';
                const shadowWidth = width * 0.67;
                const shadowHeight = 16 * scaleY;
                const shadowX = x + (width - shadowWidth) / 2;
                const shadowY = y + height - shadowHeight;
                ctx.fillRect(shadowX, shadowY, shadowWidth, shadowHeight);
                ctx.filter = 'none';
                ctx.globalAlpha = 1;
              }
            }
            
            ctx.restore();
            resolve();
          };
          assetImg.onerror = (error) => {
            console.error('Failed to load asset image for layer:', layer.id, error);
            resolve();
          };
          const imageSrc = layer.category === 'custom' ? layer.path : `./assets/${layer.path}`;
          assetImg.src = imageSrc;
        });
      } else if (layer.type === 'text') {
        ctx.save();
        
        // Get actual DOM text element position and size
        const canvasContainer = document.querySelector('.canvas-container');
        const layerElement = document.getElementById(`canvas-layer-${layer.id}`);
        const textElement = layerElement?.querySelector('p');
        
        if (canvasContainer && textElement) {
          const containerRect = canvasContainer.getBoundingClientRect();
          const textRect = textElement.getBoundingClientRect();
          
          const scaleX = 800 / containerRect.width;
          const scaleY = 800 / containerRect.height;
          
          const x = (textRect.left - containerRect.left + textRect.width/2) * scaleX;
          const y = (textRect.top - containerRect.top + textRect.height/2) * scaleY;
          
          const computedStyle = window.getComputedStyle(textElement);
          // Account for additional scaling applied to the whole layer (CSS transform: scale)
          // computedStyle.fontSize already contains the font-size defined in CSS (which includes the first scaling pass),
          // but does NOT include the transform scale defined on the parent element.
          // Multiplying by `layer.scale` reproduces that second scaling so the exported text size matches the visual canvas.
          const fontSize = parseFloat(computedStyle.fontSize) * layer.scale * scaleX;
          
          ctx.translate(x, y);
          ctx.rotate(layer.rotation * Math.PI / 180);
          if (layer.mirrored) {
            ctx.scale(-1, 1);
          }
          
          const fontFamily = availableFonts.find(f => f.name === layer.font)?.fontFamily || layer.font;
          ctx.font = `bold ${fontSize}px ${fontFamily}`;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          
          if (layer.outlineColor) {
            ctx.strokeStyle = layer.outlineColor;
            const outlineScale = layer.scale * scaleX;
            ctx.lineWidth = 2 * outlineScale;
            const baseOffsets = [
              [-1, -1], [1, -1], [-1, 1], [1, 1],
              [-2, 0], [2, 0], [0, -2], [0, 2]
            ];
            baseOffsets.forEach(([dx, dy]) => {
              ctx.strokeText(layer.text, dx * outlineScale, dy * outlineScale);
            });
          }
          
          ctx.fillStyle = layer.color;
          ctx.fillText(layer.text, 0, 0);
        }
        
        ctx.restore();
      }
    }

    const link = document.createElement('a');
    link.href = canvas.toDataURL('image/png');
    link.download = 'meme.png';
    link.click();
  }

  const value = {
    layers,
    setLayers,
    addTextLayer,
    addAssetLayer,
    clearCanvas,
    mirrorLayer,
    resizeLayer,
    removeLayer,
    downloadCanvas
  };

  return (
    <CanvasContext.Provider value={value}>
      {children}
    </CanvasContext.Provider>
  );
}

export function useCanvas() {
  const context = useContext(CanvasContext);
  if (!context) {
    throw new Error('useCanvas must be used within a CanvasProvider');
  }
  return context;
}

export default useCanvas;
